import { db, getMeta, META, setMeta, type LocalHousehold, type OutboxEntity, type OutboxOp, type OutboxOpType } from './db';
import {
  BabyRecordSchema,
  EventRecordSchema,
  MAX_FUTURE_SKEW_MS,
  fromEventRecord,
  toBabyRecord,
  toEventRecord,
  type BabyRecord,
  type EventRecord,
  type EventType,
  type LocalBaby,
  type LocalEvent,
  type PayloadFor,
} from '@/lib/schemas';
import { newId, nowIso } from '@/lib/ids';
import { isValidTimeZone } from '@/lib/time';

export class ValidationError extends Error {
  constructor(message: string, public readonly issues: string[] = []) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class ActiveSleepError extends Error {
  constructor(public readonly active: LocalEvent) {
    super('A sleep session is already running for this baby.');
    this.name = 'ActiveSleepError';
  }
}

const txTables = () => [db.events, db.babies, db.outbox, db.meta];

// ---------------------------------------------------------------------------
// Household / session context
// ---------------------------------------------------------------------------

export async function getHousehold(): Promise<LocalHousehold | null> {
  return getMeta<LocalHousehold | null>(META.household, null);
}

export async function ensureHousehold(name = 'My household'): Promise<LocalHousehold> {
  return db.transaction('rw', db.meta, async () => {
    const existing = await getHousehold();
    if (existing) return existing;
    const h: LocalHousehold = { id: newId(), name, cloud: false };
    await setMeta(META.household, h);
    return h;
  });
}

/** Enforced at the storage boundary, including stale forms and foreign IDs. */
export async function assertActiveHousehold(id: string, writable = false) {
  if (writable && await db.meta.get('privacyBusy')) throw new ValidationError('A privacy action is running. Wait for it to finish.');
  const h = await getHousehold();
  if (!h || h.id !== id) throw new ValidationError('Household changed. Reopen this action.');
  const session = await getMeta<{ userId: string } | null>(META.session, null);
  if (h.cloud && (!session || h.accountId !== session.userId)) throw new ValidationError('Sign in and validate household access first.');
  if (h.cloud && writable && !h.syncReady) throw new ValidationError('Cloud records are read-only until this household finishes loading.');
  return h;
}

async function currentActor(): Promise<string | null> {
  const s = await getMeta<{ userId: string } | null>(META.session, null);
  return s?.userId ?? null;
}

// ---------------------------------------------------------------------------
// Outbox
// ---------------------------------------------------------------------------

/**
 * Enqueue a sync operation. MUST be called inside a transaction that also
 * writes the entity, so the entity change and its outbox entry commit
 * atomically.
 *
 * Unsent (never attempted) pending ops for the same entity are coalesced:
 * they never reached the server, so merging them is safe and keeps base
 * versions correct. Ops that may have reached the server are immutable.
 *
 * Returns 'dropped' if a create+delete cancelled out entirely.
 */
export async function enqueueOp(args: {
  household_id: string;
  entity: OutboxEntity;
  entity_id: string;
  op: OutboxOpType;
  data: EventRecord | BabyRecord;
  currentServerVersion: number;
}): Promise<'queued' | 'coalesced' | 'dropped'> {
  const prior = await db.outbox.where('entity_id').equals(args.entity_id).sortBy('seq');
  const last = prior[prior.length - 1];

  if (last && !last.attempted && last.status === 'pending') {
    if (last.op === 'create') {
      if (args.op === 'delete') {
        await db.outbox.delete(last.seq!);
        return 'dropped';
      }
      await db.outbox.update(last.seq!, { data: args.data, client_ts: nowIso() });
      return 'coalesced';
    }
    if (last.op === 'update' || (last.op === 'delete' && args.op === 'update')) {
      await db.outbox.update(last.seq!, { op: args.op, data: args.data, client_ts: nowIso() });
      return 'coalesced';
    }
  }

  // Each server-applied op increments version by exactly 1.
  const base_version = last ? last.base_version + 1 : args.currentServerVersion;
  const op: OutboxOp = {
    operation_id: newId(),
    household_id: args.household_id,
    entity: args.entity,
    entity_id: args.entity_id,
    op: args.op,
    base_version,
    data: args.data,
    client_ts: nowIso(),
    retry_count: 0,
    status: 'pending',
    attempted: false,
    next_attempt_at: 0,
    last_error: null,
    server_record: null,
  };
  await db.outbox.add(op);
  return 'queued';
}

/** Server version of an entity = local version (only advanced by server acks). */
async function serverVersionOf(entity: OutboxEntity, id: string): Promise<number> {
  const row = entity === 'event' ? await db.events.get(id) : await db.babies.get(id);
  return row?.version ?? 0;
}

async function draftState(id: string) {
  const head = (await db.outbox.where('entity_id').equals(id).sortBy('seq'))[0];
  return head?.status === 'conflict' || head?.status === 'error'
    ? { sync_state: head.status, sync_error: head.last_error ?? null }
    : { sync_state: 'local' as const, sync_error: null };
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

function validateEvent(rec: EventRecord, now = Date.now()) {
  const res = EventRecordSchema.safeParse(rec);
  if (!res.success) {
    const issues = res.error.issues.map((i) => `${i.path.join('.') || 'event'}: ${i.message}`);
    throw new ValidationError(issues[0] ?? 'Invalid event', issues);
  }
  if (Date.parse(rec.started_at) > now + MAX_FUTURE_SKEW_MS)
    throw new ValidationError('Start time is too far in the future');
  return res.data as EventRecord;
}

export interface NewEventInput<T extends EventType = EventType> {
  baby_id: string;
  type: T;
  started_ms: number;
  ended_ms?: number | null;
  payload: PayloadFor<T>;
  id?: string;
  source_system?: string | null;
  source_key?: string | null;
}

export function buildEventRecord(
  input: NewEventInput,
  household_id: string,
  actor: string | null,
  nowMs = Date.now(),
): EventRecord {
  const ts = nowIso(nowMs);
  return validateEvent(
    {
      id: input.id ?? newId(),
      household_id,
      baby_id: input.baby_id,
      type: input.type,
      started_at: nowIso(input.started_ms),
      ended_at: input.ended_ms == null ? null : nowIso(input.ended_ms),
      payload: input.payload,
      version: 0,
      created_by: actor,
      updated_by: actor,
      created_at: ts,
      updated_at: ts,
      deleted_at: null,
      source_system: input.source_system ?? null,
      source_key: input.source_key ?? null,
    } as EventRecord,
    nowMs,
  );
}

async function assertBabyInHousehold(baby_id: string, household_id: string) {
  await assertActiveHousehold(household_id);
  const baby = await db.babies.get(baby_id);
  if (!baby || baby.deleted_at) throw new ValidationError('Unknown baby');
  if (baby.household_id !== household_id) throw new ValidationError('Baby belongs to a different household');
  return baby;
}

export async function createEvent(input: NewEventInput): Promise<LocalEvent> {
  const household = await ensureHousehold();
  const actor = await currentActor();
  return db.transaction('rw', txTables(), async () => {
    await assertActiveHousehold(household.id, true);
    await assertBabyInHousehold(input.baby_id, household.id);
    if (input.type === 'sleep' && input.ended_ms == null) {
      const active = await getActiveSleep(input.baby_id);
      if (active) throw new ActiveSleepError(active);
    }
    const rec = buildEventRecord(input, household.id, actor);
    const local = fromEventRecord(rec, 'local');
    await db.events.add(local);
    await enqueueOp({
      household_id: household.id,
      entity: 'event',
      entity_id: rec.id,
      op: 'create',
      data: rec,
      currentServerVersion: 0,
    });
    return local;
  });
}

export interface EventPatch {
  started_ms?: number;
  ended_ms?: number | null;
  payload?: Record<string, unknown>;
}

export async function updateEvent(id: string, patch: EventPatch): Promise<LocalEvent> {
  const actor = await currentActor();
  return db.transaction('rw', txTables(), async () => {
    const cur = await db.events.get(id);
    if (!cur || cur.deleted_at) throw new ValidationError('Event not found');
    await assertActiveHousehold(cur.household_id, true);
    await assertBabyInHousehold(cur.baby_id, cur.household_id);
    const base = toEventRecord(cur);
    const next = validateEvent({
      ...base,
      started_at: patch.started_ms !== undefined ? nowIso(patch.started_ms) : base.started_at,
      ended_at:
        patch.ended_ms === undefined ? base.ended_at : patch.ended_ms === null ? null : nowIso(patch.ended_ms),
      payload: (patch.payload ? { ...base.payload, ...patch.payload } : base.payload) as never,
      updated_by: actor,
      updated_at: nowIso(),
    } as EventRecord);
    if (next.type === 'sleep' && next.ended_at === null) {
      const active = await getActiveSleep(next.baby_id);
      if (active && active.id !== id) throw new ActiveSleepError(active);
    }
    const local = { ...fromEventRecord(next, 'local'), version: cur.version };
    await db.events.put(local);
    await enqueueOp({
      household_id: cur.household_id,
      entity: 'event',
      entity_id: id,
      op: 'update',
      data: next,
      currentServerVersion: await serverVersionOf('event', id),
    });
    const state = await draftState(id); await db.events.update(id, state);
    return { ...local, ...state };
  });
}

/** Soft delete (tombstone). Tombstones sync to other devices. */
export async function deleteEvent(id: string): Promise<void> {
  const actor = await currentActor();
  await db.transaction('rw', txTables(), async () => {
    const cur = await db.events.get(id);
    if (!cur || cur.deleted_at) return;
    await assertActiveHousehold(cur.household_id, true);
    const ts = nowIso();
    const next: EventRecord = { ...toEventRecord(cur), deleted_at: ts, updated_at: ts, updated_by: actor };
    const result = await enqueueOp({
      household_id: cur.household_id,
      entity: 'event',
      entity_id: id,
      op: 'delete',
      data: next,
      currentServerVersion: cur.version,
    });
    if (result === 'dropped') {
      // Keep a local-only recovery snapshot. It never enters the sync queue.
      await setMeta(`undoEvent:${id}`, cur);
      // Never reached the server: nothing to tombstone remotely.
      await db.events.delete(id);
    } else {
      await db.events.put({ ...fromEventRecord(next, 'local'), version: cur.version, ...await draftState(id) });
    }
  });
}

/** Restore a soft-deleted event (undo). */
export async function restoreEvent(id: string): Promise<void> {
  const actor = await currentActor();
  await db.transaction('rw', txTables(), async () => {
    const existing = await db.events.get(id);
    const snapshot = await getMeta<LocalEvent | null>(`undoEvent:${id}`, null);
    const cur = existing ?? snapshot;
    if (!cur || (existing && !existing.deleted_at)) return;
    await assertActiveHousehold(cur.household_id, true);
    if (cur.type === 'sleep' && cur.ended_at === null) {
      const active = await getActiveSleep(cur.baby_id);
      if (active && active.id !== id) throw new ActiveSleepError(active);
    }
    const next: EventRecord = { ...toEventRecord(cur), deleted_at: null, updated_at: nowIso(), updated_by: actor };
    await db.events.put({ ...fromEventRecord(next, 'local'), version: cur.version });
    await enqueueOp({
      household_id: cur.household_id,
      entity: 'event',
      entity_id: id,
      op: existing ? 'update' : 'create',
      data: next,
      currentServerVersion: cur.version,
    });
    await db.meta.delete(`undoEvent:${id}`);
    await db.events.update(id, await draftState(id));
  });
}

// ---------------------------------------------------------------------------
// Sleep
// ---------------------------------------------------------------------------

/** The open sleep session (ended_at null) for a baby, if any. */
export async function getActiveSleep(baby_id: string): Promise<LocalEvent | undefined> {
  const baby = await db.babies.get(baby_id);
  if (!baby) throw new ValidationError('Unknown baby');
  await assertBabyInHousehold(baby_id, baby.household_id);
  const sleeps = await db.events
    .where('[baby_id+type+started_ms]')
    .between([baby_id, 'sleep', -Infinity], [baby_id, 'sleep', Infinity])
    .filter((e) => !e.deleted_at && e.ended_at === null)
    .toArray();
  return sleeps.sort((a, b) => b.started_ms - a.started_ms)[0];
}

export async function startSleep(baby_id: string, atMs = Date.now()): Promise<LocalEvent> {
  return createEvent({ baby_id, type: 'sleep', started_ms: atMs, ended_ms: null, payload: { sleep_kind: null, note: '' } });
}

export async function endSleep(id: string, atMs = Date.now()): Promise<LocalEvent> {
  const cur = await db.events.get(id);
  if (!cur) throw new ValidationError('Sleep not found');
  return updateEvent(id, { ended_ms: Math.max(atMs, cur.started_ms) });
}

/** Completed or running sleeps overlapping [start,end) — for resolution UX. */
export async function findOverlappingSleeps(
  baby_id: string,
  startMs: number,
  endMs: number | null,
  excludeId?: string,
): Promise<LocalEvent[]> {
  const baby = await db.babies.get(baby_id);
  if (!baby) throw new ValidationError('Unknown baby');
  await assertBabyInHousehold(baby_id, baby.household_id);
  const end = endMs ?? Date.now();
  return db.events
    .where('[baby_id+type+started_ms]')
    .between([baby_id, 'sleep', -Infinity], [baby_id, 'sleep', end], true, false)
    .filter((e) => !e.deleted_at && e.id !== excludeId && (e.ended_ms ?? Date.now()) > startMs)
    .toArray();
}

// ---------------------------------------------------------------------------
// Babies
// ---------------------------------------------------------------------------

function validateBaby(rec: BabyRecord): BabyRecord {
  const res = BabyRecordSchema.safeParse(rec);
  if (!res.success) throw new ValidationError(res.error.issues[0]?.message ?? 'Invalid baby');
  if (!isValidTimeZone(rec.timezone)) throw new ValidationError('Unknown timezone');
  return res.data;
}

export async function createBaby(input: {
  display_name: string;
  date_of_birth: string | null;
  timezone: string;
}): Promise<LocalBaby> {
  const household = await ensureHousehold();
  return db.transaction('rw', txTables(), async () => {
    await assertActiveHousehold(household.id, true);
    const ts = nowIso();
    const rec = validateBaby({
      id: newId(),
      household_id: household.id,
      display_name: input.display_name.trim(),
      date_of_birth: input.date_of_birth || null,
      timezone: input.timezone,
      version: 0,
      created_at: ts,
      updated_at: ts,
      deleted_at: null,
    });
    const local: LocalBaby = { ...rec, sync_state: 'local' };
    await db.babies.add(local);
    await enqueueOp({
      household_id: household.id,
      entity: 'baby',
      entity_id: rec.id,
      op: 'create',
      data: rec,
      currentServerVersion: 0,
    });
    const active = await getMeta<string | null>(META.activeBabyId, null);
    if (!active) await setMeta(META.activeBabyId, rec.id);
    return local;
  });
}

export async function updateBaby(
  id: string,
  patch: Partial<Pick<BabyRecord, 'display_name' | 'date_of_birth' | 'timezone'>>,
): Promise<LocalBaby> {
  return db.transaction('rw', txTables(), async () => {
    const cur = await db.babies.get(id);
    if (!cur || cur.deleted_at) throw new ValidationError('Baby not found');
    await assertActiveHousehold(cur.household_id, true);
    const rec = validateBaby({ ...toBabyRecord(cur), ...patch, updated_at: nowIso() });
    const local: LocalBaby = { ...rec, version: cur.version, sync_state: 'local' };
    await db.babies.put(local);
    await enqueueOp({
      household_id: cur.household_id,
      entity: 'baby',
      entity_id: id,
      op: 'update',
      data: rec,
      currentServerVersion: cur.version,
    });
    const state = await draftState(id); await db.babies.update(id, state);
    return { ...local, ...state };
  });
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export async function eventsForBaby(
  baby_id: string,
  fromMs = -Infinity,
  toMs = Infinity,
  opts: { includeDeleted?: boolean } = {},
): Promise<LocalEvent[]> {
  const baby = await db.babies.get(baby_id);
  if (!baby) throw new ValidationError('Unknown baby');
  await assertBabyInHousehold(baby_id, baby.household_id);
  const rows = await db.events
    .where('[baby_id+started_ms]')
    .between([baby_id, fromMs], [baby_id, toMs], true, false)
    .toArray();
  return opts.includeDeleted ? rows : rows.filter((e) => !e.deleted_at);
}
